import { ImageViewer } from '@euler/app/components/media/ImageViewer';
import { MediaCoverView } from '@euler/app/components/media/MediaCoverView';
import { Colors } from '@euler/components/Colors';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { MediaObject } from '@euler/model/MediaObject';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { range } from 'ramda';
import { ComponentType, memo, useCallback, useMemo, useState } from 'react';
import {
  FlatList,
  NativeScrollEvent,
  NativeSyntheticEvent,
  StyleProp,
  View,
  ViewStyle,
} from 'react-native';
import { ResizeMode } from 'react-native-fast-image';

const PageIndicator = memo(
  (props: { style?: StyleProp<ViewStyle>; total: number; current: number }) => {
    const { rpx } = useResponsiveContext();
    return (
      <View
        css={`
          flex-direction: row;
          height: ${rpx(16)}px;
          border-radius: ${rpx(8)}px;
          align-items: center;
          padding-left: ${rpx(6)}px;
          padding-right: ${rpx(3)}px;
        `}
        style={props.style}
      >
        {range(0, props.total).map(i => (
          <View
            key={i}
            css={`
              width: ${rpx(8)}px;
              height: ${rpx(8)}px;
              border-radius: ${rpx(4)}px;
              background-color: ${i === props.current
                ? Colors.White
                : Colors.Gray3};
              margin-right: ${rpx(3)}px;
            `}
          />
        ))}
      </View>
    );
  },
);

export const useMediaCarousel = (medias: MediaObject[]) => {
  const navigation = useNavigation<
    StackNavigationProp<{
      _mediaPreview: {
        type: 'video' | 'photo';
        url: string;
        resizeMode?: ResizeMode | 'stretch' | 'cover' | 'contain';
      };
    }>
  >();
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = useMemo(
    () => medias.filter(x => x.type === 'image'),
    [medias],
  );
  const imageUrls = useMemo(() => images.map(x => x.url), [images]);
  const currentImageIndex = useMemo(
    () =>
      medias[currentIndex]?.type === 'image'
        ? images.findIndex(x => x.id === medias[currentIndex].id)
        : 0,
    [currentIndex, images, medias],
  );
  const onMediaPress = useCallback(
    (media: MediaObject, index?: number) => {
      if (media.type === 'image') {
        if (index != null) {
          setCurrentIndex(index);
        }
        setIsImageViewerOpen(true);
      } else {
        // todo
        navigation.navigate('_mediaPreview', {
          type: 'video',
          url: media.url,
        });
      }
    },
    [navigation],
  );

  const onScroll = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>, cellWidth: number) => {
      setCurrentIndex(Math.round(e.nativeEvent.contentOffset.x / cellWidth));
    },
    [],
  );

  const onMomentumScrollEnd = useCallback(
    (e: NativeSyntheticEvent<NativeScrollEvent>, cellWidth: number) => {
      setCurrentIndex(Math.round(e.nativeEvent.contentOffset.x / cellWidth));
    },
    [],
  );
  return {
    isImageViewerOpen,
    setIsImageViewerOpen,
    currentIndex,
    setCurrentIndex,
    onScroll,
    onMomentumScrollEnd,
    imageUrls,
    currentImageIndex,
    onMediaPress,
  };
};

export const MediaCarousel = memo(
  ({
    medias,
    style,
    itemStyle,
    showPageIndicators,
    scrollEnabled,
    playIconSize,
    playIconStyle,
    noPlaceholder,
    TouchableComponent,
    ...props
  }: {
    scrollEnabled?: boolean;
    medias: MediaObject[];
    style?: StyleProp<ViewStyle>;
    showPageIndicators?: boolean;
    TouchableComponent?: ComponentType;
    itemStyle?: StyleProp<ViewStyle>;
    listWidth?: number;
    listKey?: string;
    aspectRatio?: number;
    playIconSize?: number;
    playIconStyle?: StyleProp<ViewStyle>;
    noPlaceholder?: boolean | ((media: MediaObject) => boolean | undefined);
  } & (
    | {
        display?: 'swiper';
      }
    | {
        display: 'list';
        size?: number | { width: number; height: number };
        rowStyle?: StyleProp<ViewStyle>;
        itemSpacer?: number;
        numberOfItemsPerRow?: number;
      }
  )) => {
    const { rpx } = useResponsiveContext();

    const shouldNotDisplayPlaceholder = useCallback(
      (media: MediaObject) => {
        if (typeof noPlaceholder === 'function') {
          return noPlaceholder(media);
        }
        return noPlaceholder;
      },
      [noPlaceholder],
    );
    const {
      isImageViewerOpen,
      setIsImageViewerOpen,
      onMomentumScrollEnd,
      imageUrls,
      currentImageIndex,
      currentIndex,
      onMediaPress,
    } = useMediaCarousel(medias);

    if (!medias.length) return null;

    if (props.display === 'list') {
      const {
        size,
        numberOfItemsPerRow,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        rowStyle,
        itemSpacer = 0,
        listWidth,
        aspectRatio = 1,
      } = props;

      return (
        <LayoutProviderView passthrough={listWidth != null} style={style}>
          {layout => {
            const width = listWidth ?? layout.width;

            let mediaWidth = 0;
            let mediaHeight = 0;

            if (numberOfItemsPerRow) {
              mediaWidth = Math.max(
                0,
                (width - (numberOfItemsPerRow - 1) * itemSpacer) /
                  numberOfItemsPerRow,
              );
            } else if (size) {
              if (typeof size === 'number') {
                mediaWidth = size;
              } else {
                mediaWidth = size.width;
                mediaHeight = size.height;
              }
            } else {
              mediaWidth = width;
            }

            if (!mediaHeight) {
              mediaHeight = mediaWidth / aspectRatio;
            }

            return (
              <View
                css={`
                  flex-direction: row;
                  flex-wrap: wrap;
                `}
                style={style}
              >
                <ImageViewer
                  images={imageUrls}
                  index={currentImageIndex}
                  show={isImageViewerOpen}
                  onDismiss={() => setIsImageViewerOpen(false)}
                />
                {medias.map((item, index) => (
                  <View
                    key={item.id}
                    css={`
                      width: ${mediaWidth}px;
                      height: ${mediaHeight}px;
                      margin-right: ${itemSpacer}px;
                    `}
                    style={itemStyle}
                  >
                    <MediaCoverView
                      media={item}
                      index={index}
                      playIconSize={playIconSize}
                      playIconStyle={playIconStyle}
                      noPlaceholder={shouldNotDisplayPlaceholder(item)}
                      onPress={onMediaPress}
                      TouchableComponent={TouchableComponent}
                    />
                  </View>
                ))}
              </View>
            );
          }}
        </LayoutProviderView>
      );
    }

    return (
      <LayoutProviderView style={style}>
        {size => (
          <>
            <ImageViewer
              images={imageUrls}
              index={currentImageIndex}
              show={isImageViewerOpen}
              onDismiss={() => setIsImageViewerOpen(false)}
            />
            <FlatList
              data={medias}
              keyExtractor={item => item.id}
              horizontal
              pagingEnabled
              showsHorizontalScrollIndicator={false}
              scrollEnabled={scrollEnabled}
              // listKey={listKey}
              renderItem={({ item }) => (
                <MediaCoverView
                  media={item}
                  preferOriginalImage
                  noPlaceholder={shouldNotDisplayPlaceholder(item)}
                  playIconSize={playIconSize}
                  playIconStyle={playIconStyle}
                  onPress={onMediaPress}
                  TouchableComponent={TouchableComponent}
                  css={`
                    width: ${size.width}px;
                    height: ${size.height}px;
                  `}
                  style={itemStyle}
                />
              )}
              scrollEventThrottle={10}
              onScroll={e => onMomentumScrollEnd(e, size.width)}
              onMomentumScrollEnd={e => onMomentumScrollEnd(e, size.width)}
            />
            {showPageIndicators !== false && medias.length > 1 && (
              <View
                css={`
                  position: absolute;
                  bottom: ${rpx(10)}px;
                  left: 0;
                  right: 0;
                  justify-content: center;
                  flex-direction: row;
                `}
              >
                <PageIndicator total={medias.length} current={currentIndex} />
              </View>
            )}
          </>
        )}
      </LayoutProviderView>
    );
  },
);
